/**
 * サブドメイン操作のログ管理サービス
 */

/**
 * ログレベル
 */
export const LOG_LEVELS = {
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error',
    SUCCESS: 'success'
};

/**
 * ログをコンソールに出力
 * @param {string} level - ログレベル
 * @param {string} message - メッセージ
 * @param {Object} data - 追加データ
 */
export const log = (level, message, data = null) => {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    const logData = {
        timestamp,
        level,
        message,
        data
    };

    // コンソールに出力
    switch (level) {
        case LOG_LEVELS.ERROR:
            console.error(prefix, message, data);
            break;
        case LOG_LEVELS.WARN:
            console.warn(prefix, message, data);
            break;
        case LOG_LEVELS.SUCCESS:
            console.log(`✅ ${prefix}`, message, data);
            break;
        default:
            console.log(prefix, message, data);
    }

    // 本番環境では外部ログサービスに送信
    if (process.env.NODE_ENV === 'production') {
        sendToLogService(logData);
    }
};

/**
 * サブドメイン登録のログ
 * @param {string} subdomain - サブドメイン
 * @param {string} accountId - アカウントID
 * @param {Object} result - 登録結果
 */
export const logSubdomainRegistration = (subdomain, accountId, result) => {
    if (result.success) {
        log(LOG_LEVELS.SUCCESS, `サブドメイン登録成功: ${subdomain}.suisui.life`, {
            subdomain,
            accountId,
            dnsRecord: result.dnsRecord,
            vercelDomain: result.vercelDomain
        });
    } else {
        log(LOG_LEVELS.ERROR, `サブドメイン登録失敗: ${subdomain}.suisui.life`, {
            subdomain,
            accountId,
            error: result.error
        });
    }
};

/**
 * サブドメイン削除のログ
 * @param {string} subdomain - サブドメイン
 * @param {Object} result - 削除結果
 */
export const logSubdomainDeletion = (subdomain, result) => {
    if (result.success) {
        log(LOG_LEVELS.SUCCESS, `サブドメイン削除成功: ${subdomain}.suisui.life`, {
            subdomain,
            dnsResult: result.dnsResult,
            vercelResult: result.vercelResult
        });
    } else {
        log(LOG_LEVELS.ERROR, `サブドメイン削除失敗: ${subdomain}.suisui.life`, {
            subdomain,
            error: result.error
        });
    }
};

/**
 * API呼び出しのログ
 * @param {string} service - サービス名（Cloudflare, Vercel）
 * @param {string} action - アクション（add, remove）
 * @param {string} subdomain - サブドメイン
 * @param {Object} result - 結果
 */
export const logApiCall = (service, action, subdomain, result) => {
    const message = `${service} API ${action}: ${subdomain}.suisui.life`;

    if (result.success) {
        log(LOG_LEVELS.SUCCESS, message, {
            service,
            action,
            subdomain,
            result: result
        });
    } else {
        log(LOG_LEVELS.ERROR, message, {
            service,
            action,
            subdomain,
            error: result.error
        });
    }
};

/**
 * 外部ログサービスに送信（本番環境用）
 * @param {Object} logData - ログデータ
 */
const sendToLogService = async (logData) => {
    try {
        // ここで外部ログサービス（例：LogRocket, Sentry等）に送信
        // 現在は実装なし
        console.log('📊 外部ログサービスに送信:', logData);
    } catch (error) {
        console.error('ログ送信エラー:', error);
    }
};

/**
 * サブドメイン操作の統計情報を取得
 * @returns {Object} 統計情報
 */
export const getSubdomainStats = () => {
    // 実際の実装では、データベースから統計情報を取得
    return {
        totalRegistrations: 0,
        successfulRegistrations: 0,
        failedRegistrations: 0,
        totalDeletions: 0,
        successfulDeletions: 0,
        failedDeletions: 0
    };
};
